import { useState } from "react";
import { Modal, PasswordInput, Button, Text, Stack } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";

interface PdfPasswordModalProps {
  /**
   * Whether the modal is open
   */
  opened: boolean;
  /**
   * File name to display
   */
  fileName: string;
  /**
   * Callback when password is submitted
   */
  onSubmit: (password: string) => void;
  /**
   * Callback when modal is closed/cancelled
   */
  onCancel: () => void;
  /**
   * Whether the password submission is in progress
   */
  isLoading?: boolean;
  /**
   * Error message to display (e.g., "Incorrect password")
   */
  error?: string | null;
}

/**
 * Modal for prompting user to enter a password for a password-protected PDF
 */
export const PdfPasswordModal: React.FC<PdfPasswordModalProps> = ({
  opened,
  fileName,
  onSubmit,
  onCancel,
  isLoading = false,
  error = null,
}) => {
  const [password, setPassword] = useState("");

  const handleSubmit = () => {
    if (password.trim()) {
      onSubmit(password);
    }
  };

  const handleClose = () => {
    setPassword("");
    onCancel();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="File is Locked"
      centered
    >
      <Stack gap="md">
        <Text size="sm">
          <strong>{fileName}</strong> is password protected. Please enter the
          password to unlock it.
        </Text>

        <PasswordInput
          label="Password"
          placeholder="Enter password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && password.trim()) {
              handleSubmit();
            }
          }}
          leftSection={<IconLock size={16} />}
          error={error}
          disabled={isLoading}
          autoFocus
        />

        <div
          style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}
        >
          <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!password.trim() || isLoading}
            loading={isLoading}
          >
            Submit
          </Button>
        </div>
      </Stack>
    </Modal>
  );
};
